import { defineEvent } from "@amtana/module";
import { z } from "zod";

import { batchFailureReasonSchema, runFailureCodeSchema } from "./analysis.schemas.ts";

// The facts of an analysis run (adrs/001): one stream per run,
// `analysis-run:<analysisId>.<runNumber>`. They live in the contract so peers
// can react: prospect saves the leads a batch found. Profiles travel inside
// the events, so a refold never calls HikerAPI again.

export const ANALYSIS_RUN = "analysis-run";

export const memberSchema = z.object({
  id: z.string(),
  username: z.string(),
  fullName: z.string(),
  isPrivate: z.boolean(),
  isVerified: z.boolean(),
});
export type Member = z.infer<typeof memberSchema>;

export const enrichedProfileSchema = memberSchema.extend({
  followers: z.number().int().nonnegative(),
  following: z.number().int().nonnegative(),
  biography: z.string(),
  website: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  category: z.string().nullable(),
  isBusiness: z.boolean(),
  city: z.string().nullable(),
});
export type EnrichedProfile = z.infer<typeof enrichedProfileSchema>;

export const memberListSchema = z.enum(["followers", "following"]);
export type MemberList = z.infer<typeof memberListSchema>;

export const runPauseReasonSchema = z.enum(["manual", "credit-limit", "stopped-for-another"]);

export const runStarted = defineEvent(
  ANALYSIS_RUN,
  "run.started",
  z.object({
    analysisId: z.string(),
    ownerId: z.string(),
    username: z.string(),
    list: memberListSchema,
    /** The plan's profiles per analysis; null for no cap. */
    profileCap: z.number().int().positive().nullable(),
    /** The run's credit limit; null for none. */
    creditLimit: z.number().int().positive().nullable(),
    costPerLead: z.number().int().positive(),
    /** Enrichment batches in flight at once. */
    window: z.number().int().positive(),
  }),
);

export const targetResolved = defineEvent(
  ANALYSIS_RUN,
  "target.resolved",
  z.object({
    accountId: z.string(),
    followers: z.number().int().nonnegative(),
    following: z.number().int().nonnegative(),
  }),
);

export const pageFetched = defineEvent(
  ANALYSIS_RUN,
  "page.fetched",
  z.object({
    page: z.number().int().positive(),
    cursor: z.string().nullable(),
    next: z.string().nullable(),
    members: z.array(memberSchema),
  }),
);

export const pageFailed = defineEvent(
  ANALYSIS_RUN,
  "page.failed",
  z.object({
    page: z.number().int().positive(),
    cursor: z.string().nullable(),
    reason: batchFailureReasonSchema,
  }),
);

export const batchEnriched = defineEvent(
  ANALYSIS_RUN,
  "batch.enriched",
  z.object({
    batch: z.string(),
    page: z.number().int().positive(),
    analysisId: z.string(),
    ownerId: z.string(),
    profiles: z.array(enrichedProfileSchema),
    /** Members the lookup found nothing for. */
    missing: z.number().int().nonnegative(),
    /** Members HikerAPI kept failing on; the scan queues them again (ADR-002, point 5). Left
     * off when there are none, as the Go worker's `omitempty` left it, so either worker writes
     * the same fact; a reader takes a missing list as empty (D1425). */
    deferred: z.array(memberSchema).optional(),
    /** Credits the account gave for these leads. */
    charged: z.number().int().nonnegative(),
    /** The account had fewer credits than the leads cost. */
    short: z.boolean(),
  }),
);

export const batchFailed = defineEvent(
  ANALYSIS_RUN,
  "batch.failed",
  z.object({ batch: z.string(), page: z.number().int().positive(), reason: batchFailureReasonSchema }),
);

export const runPaused = defineEvent(ANALYSIS_RUN, "run.paused", z.object({ reason: runPauseReasonSchema }));
export const runResumed = defineEvent(ANALYSIS_RUN, "run.resumed", z.object({}));
/**
 * `analysisId`/`ownerId` let a subscriber (S7.08's notification trigger) name who to notify
 * without reading the process manager's own state; runs from before them carry neither.
 */
export const runCompleted = defineEvent(
  ANALYSIS_RUN,
  "run.completed",
  z.object({ analysisId: z.string().optional(), ownerId: z.string().optional() }),
);
/** `analysisId`/`ownerId` (finding 9, D533): see `runCompleted`, added the same way so the
 * router's own scan-owner rule can name who to notify without reading the process manager's
 * state; a run from before them carries neither, and hears nothing (`scanOutOfCreditsRule`). */
export const runOutOfCredits = defineEvent(
  ANALYSIS_RUN,
  "run.out-of-credits",
  z.object({ analysisId: z.string().optional(), ownerId: z.string().optional() }),
);
/**
 * The run stopped, as a code and nothing else (packages/module ADR-001, revision 1): what
 * happened in words is on the error line in the log. Runs from before codes may carry a free
 * `reason` or `detail`; readers ignore both and read the missing code as "unknown".
 * `analysisId`/`ownerId`: see `runCompleted`.
 */
export const runFailed = defineEvent(
  ANALYSIS_RUN,
  "run.failed",
  z.object({
    code: runFailureCodeSchema.optional(),
    analysisId: z.string().optional(),
    ownerId: z.string().optional(),
  }),
);

export const analysisRunEvents = [
  runStarted,
  targetResolved,
  pageFetched,
  pageFailed,
  batchEnriched,
  batchFailed,
  runPaused,
  runResumed,
  runCompleted,
  runOutOfCredits,
  runFailed,
] as const;
